import { useCallback, useEffect, useRef, useState } from 'react'
import { Inviter, SessionState, UserAgent } from 'sip.js'
import config from '@/config'

/**
 * The browser as the phone for an outbound call.
 *
 * SIP over WebSocket to kamailio, the same edge the venue's inbound calls use.
 * The one thing that makes this an outbound call is the call token in the
 * INVITE: the operator minted it for exactly this destination seconds ago,
 * kamailio checks it with the operator, and Asterisk dials the guest through
 * the carrier as the venue. The browser never learns anything about the
 * routing; it presents a token and hears ringing.
 */

export type SoftphoneState = 'idle' | 'connecting' | 'ringing' | 'live' | 'ending' | 'failed'

export function useSoftphone() {
  const [state, setState] = useState<SoftphoneState>('idle')
  const [seconds, setSeconds] = useState(0)
  const [muted, setMuted] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const agentRef = useRef<UserAgent | null>(null)
  const inviterRef = useRef<Inviter | null>(null)

  useEffect(() => {
    if (state !== 'live') return undefined
    setSeconds(0)
    const tick = setInterval(() => setSeconds((s) => s + 1), 1000)
    return () => clearInterval(tick)
  }, [state])

  const teardown = useCallback(async () => {
    const inviter = inviterRef.current
    inviterRef.current = null
    try {
      if (inviter) {
        if (inviter.state === SessionState.Established) await inviter.bye()
        else if (inviter.state === SessionState.Establishing || inviter.state === SessionState.Initial) await inviter.cancel()
      }
    } catch {
      // the session may already be gone
    }
    try {
      await agentRef.current?.stop()
    } catch {
      // transport already closed
    }
    agentRef.current = null
    setMuted(false)
  }, [])

  // never leave a call up when the component that owns this unmounts
  useEffect(() => {
    return () => {
      void teardown()
    }
  }, [teardown])

  const hangUp = useCallback(async () => {
    setState('ending')
    await teardown()
    setState('idle')
  }, [teardown])

  const toggleMute = useCallback(() => {
    const sdh = inviterRef.current?.sessionDescriptionHandler as unknown as { peerConnection?: RTCPeerConnection } | undefined
    const pc = sdh?.peerConnection
    if (!pc) return
    const next = !muted
    pc.getSenders().forEach((sender) => {
      if (sender.track?.kind === 'audio') sender.track.enabled = !next
    })
    setMuted(next)
  }, [muted])

  const dial = useCallback(
    async ({ to, token, identity }: { to: string; token: string; identity: string }) => {
      setFailure(null)
      setState('connecting')
      try {
        const server = config.url.voiceWs
        const domain = server.replace('wss://', '')
        const uri = UserAgent.makeURI(`sip:${identity}@${domain}`)
        const target = UserAgent.makeURI(`sip:${to}@${domain}`)
        if (!uri || !target) throw new Error('bad SIP URI')

        const agent = new UserAgent({ uri, transportOptions: { server }, logLevel: 'error' })
        agentRef.current = agent
        await agent.start()

        const inviter = new Inviter(agent, target, {
          extraHeaders: [`X-Stampede-Call-Token: ${token}`],
          sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
        })
        inviterRef.current = inviter

        inviter.stateChange.addListener((s: SessionState) => {
          if (s === SessionState.Established) {
            const sdh = inviter.sessionDescriptionHandler as unknown as { peerConnection?: RTCPeerConnection }
            const pc = sdh.peerConnection
            if (pc && audioRef.current) {
              const remote = new MediaStream()
              pc.getReceivers().forEach((r) => {
                if (r.track) remote.addTrack(r.track)
              })
              audioRef.current.srcObject = remote
              void audioRef.current.play()
            }
            setState('live')
          }
          if (s === SessionState.Terminated) {
            setState((prev) => (prev === 'ringing' || prev === 'connecting' ? 'failed' : 'idle'))
            void teardown()
          }
        })

        setState('ringing')
        await inviter.invite({
          requestDelegate: {
            onReject: (response) => {
              // kamailio's refusals carry a reason phrase worth showing
              const phrase = response.message.reasonPhrase
              setFailure(phrase && phrase !== 'Not Found' ? phrase : 'The call could not be placed.')
            },
          },
        })
      } catch (err) {
        setFailure((err as Error).message || 'The call could not be placed.')
        await teardown()
        setState('failed')
      }
    },
    [teardown]
  )

  return { state, seconds, muted, failure, audioRef, dial, hangUp, toggleMute }
}
